import { useState, type FormEvent } from 'react';
import { funnelStep } from '../../analytics/funnel';
import { Button, TextField } from '../../ui';

/**
 * "What stopped you?" — asked ONCE per visitor, when they put the trial offer away (trial14-and-demo
 * handoff §3, 2026-10-12: 5 saw the 3-day offer and 0 started it, and nothing said why). Logged as
 * `offer_exit_reason` to our own log only (`pixel: false`): the "Something else…" words never reach
 * Meta. Closing it answers nothing and is never asked again either.
 */
const KEY = 'saddle.offerExitAsked';

export function exitQuestionAsked(): boolean {
  try {
    return localStorage.getItem(KEY) !== null;
  } catch {
    return true; // storage refused: never risk asking twice
  }
}

function markAsked(): void {
  try {
    localStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* see exitQuestionAsked */
  }
}

const ANSWERS = [
  { reason: 'price', label: 'Price' },
  { reason: 'card', label: 'Card up front' },
  { reason: 'not_sure', label: 'Not sure it’s for me' },
  { reason: 'just_looking', label: 'Just looking' },
] as const;

export default function ExitQuestion({ onDone }: { onDone: () => void }) {
  const [other, setOther] = useState(false);
  const [text, setText] = useState('');

  function answer(reason: (typeof ANSWERS)[number]['reason'] | 'other', words?: string): void {
    markAsked();
    const said = (words ?? '').trim().slice(0, 140);
    funnelStep('offer_exit_reason', { reason, ...(said ? { text: said } : {}) }, { pixel: false });
    onDone();
  }

  function close(): void {
    markAsked();
    onDone();
  }

  function submitOther(e: FormEvent): void {
    e.preventDefault();
    answer('other', text);
  }

  return (
    <section className="exit-question" aria-labelledby="exit-question-title">
      <h2 id="exit-question-title" className="type-title exit-question-title">What stopped you?</h2>
      {!other ? (
        <div className="exit-question-answers">
          {ANSWERS.map((a) => (
            <Button key={a.reason} title={a.label} variant="secondary" onPress={() => answer(a.reason)} />
          ))}
          <Button title="Something else…" variant="secondary" onPress={() => setOther(true)} />
        </div>
      ) : (
        <form onSubmit={submitOther} className="exit-question-other">
          <TextField label="Tell us (optional)" value={text} onChangeText={(v) => setText(v.slice(0, 140))} maxLength={140} />
          <Button title="Send" type="submit" />
        </form>
      )}
      <button type="button" className="link-button exit-question-close" onClick={close}>Close</button>
    </section>
  );
}
